import { db } from '../firebaseConfig';
import { collection, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

/**
 * Service to handle workout log persistence.
 */
export const logWorkoutSession = async (logData) => {
  try {
    // Structure expected from the UI
    const entry = {
      exerciseId: logData.exerciseId,
      profileId: logData.profileId,
      ownerUserId: logData.ownerUserId,
      // date normally uses serverTimestamp() for integrity. Adding a set to a
      // PAST workout (see WorkoutLog.jsx's "Add Set" on a day/routine group)
      // needs to backdate into that day instead — logData.date, when
      // provided, is a plain JS Date and gets wrapped as a Firestore
      // Timestamp so it sorts/groups identically to a server-written one.
      date: logData.date ? Timestamp.fromDate(logData.date) : serverTimestamp(),
      note: logData.note || ""
    };

    // Two mutually exclusive log shapes, chosen by the caller: a strength
    // set (weight/reps) or a cardio session (durationMinutes + optional
    // distance, added for the Cardio exercise category). Each field is
    // guarded by typeof (not truthiness) since a bodyweight set's weight
    // or a 0-distance cardio session are legitimate values, not "absent" --
    // only genuinely omitted fields (the other shape's fields) are skipped,
    // so neither write path has to carry the other's unused fields.
    if (typeof logData.weight === "number") entry.weight = logData.weight;
    if (typeof logData.reps === "number") entry.reps = logData.reps;
    if (typeof logData.durationMinutes === "number") entry.durationMinutes = logData.durationMinutes;
    if (typeof logData.distance === "number") entry.distance = logData.distance;

    // Optional PR flag. Only persisted when explicitly provided so the
    // existing session-start / per-set writes stay byte-for-byte unchanged.
    if (typeof logData.isPR === "boolean") {
      entry.isPR = logData.isPR;
    }

    // Optional routine attribution. Only persisted when provided (mission
    // per-set writes) so the generic "Log a Workout" form and SESSION_START
    // markers aren't forced to carry an empty field. Absent routineName is how
    // the history UI identifies "Freestyle" (unattributed / pre-existing) logs.
    if (typeof logData.routineName === "string" && logData.routineName) {
      entry.routineName = logData.routineName;
    }

    const docRef = await addDoc(collection(db, "logs"), entry);

    console.log("Mission Log Stored: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Misattribution recovery: reassign a single log to a different Hero
 * (profileId) within the same Manager account. Only `logs` carry a
 * profileId — `routines` are shared across all of an account's Heroes by
 * design (see CLAUDE.md, Profile Management), so there's nothing to
 * reassign there. Firestore rules already permit this: the `logs` block's
 * `allow read, write: if ... request.auth.uid == resource.data.ownerUserId`
 * covers update, and this only ever changes profileId, never ownerUserId, so
 * no rules change was needed.
 */
export const reassignLogProfile = async (logId, newProfileId) => {
  try {
    await updateDoc(doc(db, "logs", logId), { profileId: newProfileId });
    console.log("Log Reassigned: ", logId, "->", newProfileId);
    return { success: true };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Edit an existing logged set/session in place (live-mission row or
 * past-history entry — same doc shape, same rule regardless of surface).
 * Takes a plain fields object rather than positional weight/reps/isPR args
 * so the same function covers both log shapes -- a strength edit passes
 * `{weight, reps, isPR}` (callers are responsible for recomputing isPR
 * against the exercise's max *excluding this log*, see firestoreUtils.js's
 * getExerciseMax excludeLogId param, so a corrected weight never leaves a
 * stale PR badge), a cardio edit passes `{durationMinutes, distance}` where
 * `distance` may be `deleteField()` (imported by the caller from
 * firebase/firestore) to clear a previously-set distance rather than
 * leaving a stale value when the field is edited back to blank.
 */
export const updateLogSet = async (logId, fields) => {
  try {
    await updateDoc(doc(db, "logs", logId), fields);
    console.log("Log Set Updated: ", logId);
    return { success: true };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};

/**
 * Delete a single logged set — from a live mission, Quick Start, or past
 * history. No isPR recompute for any OTHER set is triggered here, matching
 * the existing "isPR is a permanent record of the moment it was logged"
 * convention already established for edits.
 */
export const deleteLogSet = async (logId) => {
  try {
    await deleteDoc(doc(db, "logs", logId));
    console.log("Log Set Deleted: ", logId);
    return { success: true };
  } catch (error) {
    console.error("Transmission Error: ", error);
    return { success: false, error };
  }
};